import { Theme } from '@busybusy/webapp-react-ui';
import { PhotoIcon } from 'assets/icons';
import classNames from 'classnames';
import { dashboardCardBarChartGradientBlueLightPurple } from 'components/domain/dashboard/dashboard-card-chart/DashboardBarChart/constants/constants';
import DashboardCardBarChart from 'components/domain/dashboard/dashboard-card-chart/DashboardBarChart/DashboardBarChart';
import { IDashboardSettingsItem } from 'components/domain/dashboard/DashboardSettingsItem/DashboardSettingsItem';
import DashboardContentCard from 'components/foundation/dashboard/DashboardContentCard/DashboardContentCard';
import DashboardEnableCard from 'components/foundation/dashboard/DashboardEnableCard/DashboardEnableCard';
import useDashboardGraphDates from 'containers/dashboard/hooks/useDashboardGraphDates';
import {
  assignItemsByCreationToRanges,
  getDashboardTimeFrameTitle,
  zipDaysToValue,
} from 'containers/dashboard/util/utils';
import usePhotoVerificationReportData, {
  IPhotoVerificationData,
} from 'containers/photo-verification-report/hooks/usePhotoVerificationReportData';
import useLegacyNavigation from 'hooks/navigation/useLegacyNavigation';
import usePhotoVerificationReportNavigation from 'hooks/navigation/usePhotoVerificationReportNavigation';
import { t } from 'i18next';
import { last } from 'lodash';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import PhotoType from 'types/enum/PhotoType';
import TimeRangeType from 'types/TimeRangeType';
import { useFeatureFlags } from 'utils/features';
import useDashboardCardTotal from '../../hooks/useDashboardCardTotal';

export interface IPhotoVerificationDashboardCardProps {
  className?: string | object;
  theme: Theme.DARK | Theme.LIGHT;
  setting: IDashboardSettingsItem;
  onHide: () => void;
}

const PhotoVerificationDashboardCard = (props: IPhotoVerificationDashboardCardProps) => {
  const { className, theme, setting, onHide } = props;
  const timeFrame = setting.options?.time;
  const { timeRange, timeRanges } = useDashboardGraphDates(timeFrame !== 'daily' ? timeFrame : 'pastSeven')!;
  const [failedMatches, setFailedMatches] = useState<IPhotoVerificationData>();
  const total = useDashboardCardTotal(
    timeRanges,
    null,
    null,
    zipDaysToValue(assignItemsByCreationToRanges(timeRanges, failedMatches?.data ?? [])) ?? [],
    setting.options?.time ?? 'payPeriod',
    false
  );
  const photoVerificationNavigation = usePhotoVerificationReportNavigation();
  const isPro = useFeatureFlags('PRO');
  const legacyNavigation = useLegacyNavigation();

  const fetchPhotoVerificationData = usePhotoVerificationReportData({
    currentTimeRangeType: null,
    memberId: null,
    timeRange,
    photoType: PhotoType.FAILED,
  });

  useEffect(() => {
    setData();
  }, [setting]);

  async function setData() {
    const data = await fetchPhotoVerificationData();
    setFailedMatches(data);
  }

  const getTimeRangeType = () => {
    const type = setting.options?.time ?? 'payPeriod';
    switch (type) {
      case 'payPeriod':
        return TimeRangeType.PAY_PERIOD;
      case 'monthly':
        return TimeRangeType.MONTHLY;
      case 'weekly':
        return TimeRangeType.WEEKLY;
      case 'daily':
        return TimeRangeType.DAILY;
      default:
        return TimeRangeType.CUSTOM;
    }
  };

  const getNavRange = () => {
    let navRange = timeRange;
    if (setting.options?.time !== 'pastSeven' && setting.options?.time !== 'pastFourteen') {
      const lastRange = last(timeRanges);
      if (lastRange) {
        navRange = {
          startTime: DateTime.fromSeconds(lastRange.startTime, { zone: 'utc' }),
          endTime: DateTime.fromSeconds(lastRange.endTime, { zone: 'utc' }),
        };
      }
    }
    return navRange;
  };

  const handlePhotoVerificationNavigation = () => {
    const navRange = getNavRange();
    photoVerificationNavigation({
      start_date: navRange.startTime.toISODate(),
      end_date: navRange.endTime.toISODate(),
      time_range_type: getTimeRangeType(),
    });
  };

  const classes = classNames('photo-verification-dashboard-card', className);

  return (
    <>
      {isPro ? (
        <DashboardContentCard
          header={total}
          theme={theme}
          subheaderValue={getDashboardTimeFrameTitle(
            t('Photo Verification (Failed Matches)'),
            setting.options?.time ?? 'monthly'
          )}
          className={classes}
          rightLabel={t('Details')}
          onRightLabelClick={handlePhotoVerificationNavigation}
        >
          <DashboardCardBarChart
            data={zipDaysToValue(assignItemsByCreationToRanges(timeRanges, failedMatches?.data ?? []))}
            dataKey="value"
            valueType="raw"
            timeFrame={timeFrame}
            theme={theme}
            {...dashboardCardBarChartGradientBlueLightPurple}
          />
        </DashboardContentCard>
      ) : (
        <DashboardEnableCard
          setting={setting}
          svg={PhotoIcon}
          buttonLabel={t('Upgrade to Pro')}
          theme={theme}
          onHide={onHide}
          onClick={legacyNavigation.subscription}
        />
      )}
    </>
  );
};

export default PhotoVerificationDashboardCard;
